import { useState, useEffect } from 'react';
import {
  X,
  Edit,
  Shield,
  CheckCircle,
  AlertCircle,
  Link as LinkIcon,
  AlertTriangle,
  CircleCheck,
  RefreshCw,
} from 'lucide-react';
import type { Pet } from '../../types/pet';
import { ReportLostModal } from './ReportLostModal';

type LostStatus = 'active' | 'lost' | 'recovered';

interface FamilyMember {
  id: string;
  did?: string;
  ancestryDid?: string;
  name: string;
  species: string;
  breed?: string;
  birthYear?: string;
  color?: string;
  breederName?: string;
  notes?: string;
  relationship: string;
  verificationType: 'verified' | 'owner-added' | 'ancestry-did';
  confidence?: 'certain' | 'estimated' | 'unknown';
}

interface ViewFamilyMemberModalProps {
  member: FamilyMember;
  /** When member is one of the user's pets, pass the full Pet so we can show/update Pet Status (alive/deceased/lost). */
  pet?: Pet | null;
  onClose: () => void;
  onEdit: (member: FamilyMember) => void;
  onRemove: (memberId: string) => void;
  onUpgradeToDID: (memberId: string) => void;
  /** Update pet status – same as Overview "Pet Status". Pass when pet is provided. */
  onUpdatePet?: (petId: string, updates: Partial<Pet>) => void;
  /** Called after successfully reporting pet as lost (e.g. refresh list). Pass when pet is provided. */
  onReportLostSuccess?: () => void;
  /** Mark pet as found (API). Pass when pet is provided. */
  onMarkFound?: (petId: string) => Promise<void>;
}

export function ViewFamilyMemberModal({
  member,
  pet,
  onClose,
  onEdit,
  onRemove: _onRemove,
  onUpgradeToDID,
  onUpdatePet,
  onReportLostSuccess,
  onMarkFound,
}: ViewFamilyMemberModalProps) {
  const [isDeceased, setIsDeceased] = useState<boolean>(
    pet?.isDeceased ?? false
  );
  const [lostStatus, setLostStatus] = useState<LostStatus>(
    pet?.lostStatus ?? 'active'
  );
  const [lostLoading, setLostLoading] = useState(false);
  const [showReportLostModal, setShowReportLostModal] = useState(false);

  useEffect(() => {
    if (pet) {
      setIsDeceased(pet.isDeceased ?? false);
      setLostStatus((pet.lostStatus as LostStatus) ?? 'active');
    }
  }, [pet?.id, pet?.isDeceased, pet?.lostStatus]);

  const getVerificationBadge = () => {
    switch (member.verificationType) {
      case 'verified':
        return (
          <div className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
            <CheckCircle className="w-3 h-3" />
            Verified DID
          </div>
        );
      case 'ancestry-did':
        return (
          <div className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
            <Shield className="w-3 h-3" />
            Ancestry DID
          </div>
        );
      case 'owner-added':
        return (
          <div className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
            <AlertCircle className="w-3 h-3" />
            Owner-added
            {member.confidence && ` • ${member.confidence}`}
          </div>
        );
    }
  };

  const handlePetStatusChange = (deceased: boolean) => {
    setIsDeceased(deceased);
    if (pet && onUpdatePet) {
      onUpdatePet(pet.id, {
        isDeceased: deceased,
        deceasedAt: deceased ? new Date().toISOString() : undefined,
      });
    }
  };

  const handleMarkFound = async () => {
    if (!pet || !onMarkFound) return;
    setLostLoading(true);
    try {
      await onMarkFound(pet.id);
      setLostStatus('recovered');
    } finally {
      setLostLoading(false);
    }
  };

  const showPetStatus = Boolean(pet && onUpdatePet);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border dark:border-white/8">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl mb-1">Family Member Details</h2>
              <p className="text-sm opacity-90">{member.relationship}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Basic Info */}
          <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-xl p-4 mb-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center text-3xl">
                  {member.species?.toLowerCase() === 'dog'
                    ? '🐕'
                    : member.species?.toLowerCase() === 'cat'
                      ? '🐈'
                      : '🐾'}
                </div>
                <div>
                  <h3 className="text-xl mb-1 dark:text-white">
                    {member.name}
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-white/70">
                    {member.breed || member.species}
                  </div>
                </div>
              </div>
              {getVerificationBadge()}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500 dark:text-white/50 mb-1">
                  Species
                </div>
                <div className="text-gray-900 dark:text-white">
                  {member.species}
                </div>
              </div>
              {member.breed && (
                <div>
                  <div className="text-gray-500 dark:text-white/50 mb-1">
                    Breed
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {member.breed}
                  </div>
                </div>
              )}
              {member.birthYear && (
                <div>
                  <div className="text-gray-500 dark:text-white/50 mb-1">
                    Birth Year
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {member.birthYear}
                  </div>
                </div>
              )}
              {member.color && (
                <div>
                  <div className="text-gray-500 dark:text-white/50 mb-1">
                    Color / Markings
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {member.color}
                  </div>
                </div>
              )}
              {member.breederName && (
                <div>
                  <div className="text-gray-500 dark:text-white/50 mb-1">
                    Breeder
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {member.breederName}
                  </div>
                </div>
              )}
              <div>
                <div className="text-gray-500 dark:text-white/50 mb-1">
                  Relationship
                </div>
                <div className="text-gray-900 dark:text-white">
                  {member.relationship}
                </div>
              </div>
            </div>
          </div>

          {/* DID Information */}
          {(member.did || member.ancestryDid) && (
            <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-700 dark:text-white/70 mb-2">
                {member.verificationType === 'verified'
                  ? 'Verified DID'
                  : 'Ancestry DID'}
              </div>
              <div className="font-mono text-xs break-all text-gray-900 dark:text-white bg-white dark:bg-[#16151D] p-2 rounded">
                {member.did || member.ancestryDid}
              </div>
              {member.verificationType === 'verified' && (
                <div className="flex items-center gap-2 mt-2 text-sm text-green-600 dark:text-green-400">
                  <LinkIcon className="w-4 h-4" />
                  <span>Blockchain Verified</span>
                </div>
              )}
            </div>
          )}

          {/* Pet Status – same as Overview (Alive / Deceased, Lost) – only when member is one of our pets */}
          {showPetStatus && (
            <div className="border border-gray-200 dark:border-white/8 rounded-xl p-4 mb-4 space-y-4">
              <div className="text-sm font-medium text-gray-700 dark:text-white/90">
                Pet Status
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="petStatusModal"
                    checked={!isDeceased}
                    onChange={() => handlePetStatusChange(false)}
                    className="rounded-full"
                  />
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="dark:text-white">Alive</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="petStatusModal"
                    checked={isDeceased}
                    onChange={() => handlePetStatusChange(true)}
                    className="rounded-full"
                  />
                  <span className="w-3 h-3 rounded-full bg-gray-500 dark:bg-gray-400" />
                  <span className="dark:text-white">Deceased</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-white/50">
                Same as Overview. Marking as deceased preserves records and
                pauses active activities.
              </p>

              <div className="pt-3 border-t border-gray-200 dark:border-white/8">
                <div className="text-sm text-gray-500 dark:text-white/50 mb-2">
                  Lost Pet Status
                </div>
                {lostStatus === 'lost' ? (
                  <>
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/30 mb-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                            {member.name} is currently reported as lost
                          </p>
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            Your pet is visible in the Global Pet Recovery
                            search.
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleMarkFound}
                      disabled={lostLoading || isDeceased}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                    >
                      {lostLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CircleCheck className="w-4 h-4" />
                      )}
                      Pet is Found – Mark as Not Lost
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowReportLostModal(true)}
                      disabled={isDeceased}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      Report Pet as Lost
                    </button>
                    <p className="text-xs text-gray-500 dark:text-white/50 mt-2">
                      {isDeceased
                        ? 'Cannot report a deceased pet as lost.'
                        : 'Reporting as lost will make your pet searchable in the Global Pet Recovery system.'}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {member.notes && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4 mb-4">
              <div className="text-sm text-blue-900 dark:text-blue-300 mb-2">
                Notes / History
              </div>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                {member.notes}
              </div>
            </div>
          )}

          {/* Confidence Level */}
          {member.confidence && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4 mb-4">
              <div className="text-sm text-yellow-900 dark:text-yellow-200 mb-1">
                Confidence Level
              </div>
              <div className="text-sm text-yellow-800 dark:text-yellow-300 capitalize">
                {member.confidence}
              </div>
              <div className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                {member.confidence === 'certain' &&
                  'Documented or directly known relationship'}
                {member.confidence === 'estimated' &&
                  'Based on provided information or visual similarity'}
                {member.confidence === 'unknown' &&
                  'Limited information available'}
              </div>
            </div>
          )}

          {/* Upgrade Option */}
          {member.verificationType === 'owner-added' && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-indigo-900 dark:text-indigo-200 mb-1">
                    Upgrade to Ancestry DID
                  </div>
                  <div className="text-xs text-indigo-700 dark:text-indigo-300 mb-3">
                    Create a blockchain-verified Ancestry DID for future linking
                    and verification.
                  </div>
                  <button
                    onClick={() => {
                      onUpgradeToDID(member.id);
                      onClose();
                    }}
                    className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Create Ancestry DID
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions – Edit Details only; no Remove from Family */}
          <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-4 border border-gray-200 dark:border-white/8">
            <div className="text-sm text-gray-700 dark:text-white/70 mb-3">
              Manage Family Member
            </div>
            <div className="space-y-2">
              <button
                onClick={() => {
                  onEdit(member);
                  onClose();
                }}
                className="w-full flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#16151D] border border-gray-300 dark:border-white/8 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-900 dark:text-white"
              >
                <Edit className="w-4 h-4 text-gray-600 dark:text-white/70" />
                <span>Edit Details</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 dark:bg-[#0F0E16] border-t border-gray-200 dark:border-white/8">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            Close
          </button>
        </div>
      </div>

      {/* Report Lost Modal – same as Overview section */}
      {showReportLostModal && pet && (
        <ReportLostModal
          pet={pet}
          onClose={() => setShowReportLostModal(false)}
          onSuccess={() => {
            setLostStatus('lost');
            setShowReportLostModal(false);
            onReportLostSuccess?.();
          }}
        />
      )}
    </div>
  );
}
